/**
 * How It Works Together — ingredient wheel + detail card (desktop) /
 * ingredient modal carousel (mobile, <768px).
 *
 * Desktop (>=1120px): clicking a node rotates the entire wheel (every node
 * shifts together along the circle) until the clicked ingredient lands on
 * the fixed active slot at 90deg (3 o'clock), matching the card beside it
 * — not a simple position swap.
 *
 * Tablet (768px-1119px): the card stacks below the wheel with its tail
 * pointing up instead (see pdm-how-it-works-together.css), so the active
 * slot moves to 180deg (6 o'clock) to stay aligned with the tail.
 *
 * Mobile (<768px): tapping a node opens a Swiper-powered modal with the
 * same ingredient detail, looping prev/next navigation and dot indicators.
 *
 * Ingredient counts 1 and 2:
 * - 2 ingredients on mobile keep the active slot at 90deg (3 o'clock) so the
 *   two nodes sit left and right as designed, not top and bottom at 180deg.
 *   Tablet and desktop are unchanged.
 * - The modal only loops with 3 or more slides. Swiper's loop mode needs
 *   spare slides to clone, so with 1 or 2 it does not loop; with 2 it
 *   rewinds from the last slide back to the first instead.
 */

(() => {
  const MOBILE_BREAKPOINT = 768;
  // Same threshold as .content / .card-tail in pdm-how-it-works-together.css.
  // If that CSS value changes, update this one too.
  const STACKED_BREAKPOINT = 1120;

  const isMobileViewport = () =>
    window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`).matches;

  const isStackedViewport = () =>
    window.matchMedia(`(max-width: ${STACKED_BREAKPOINT - 1}px)`).matches;

  const getActiveAngle = (nodeCount) => {
    if (nodeCount === 2 && isMobileViewport()) return 90;
    return isStackedViewport() ? 180 : 90;
  };

  const normalizeAngleDelta = (delta) => {
    let normalized = delta % 360;
    if (normalized > 180) normalized -= 360;
    if (normalized <= -180) normalized += 360;
    return normalized;
  };

  // Exactly 3 ingredients use a fixed T instead of an evenly spaced ring.
  // Desktop/tablet: active node in the active slot, the next one opposite it,
  // the last one a quarter turn back (right, left, top at 3 o'clock).
  // Mobile (<768px): fixed by block order, left, top, right; nothing rotates.
  const placeThree = (nodes, activeIndex, instant = false) => {
    let targets;
    if (isMobileViewport()) {
      targets = [270, 0, 90];
    } else {
      const a = getActiveAngle(3);
      const slots = [a, a + 180, a - 90];
      targets = [];
      for (let k = 0; k < 3; k += 1) targets[(activeIndex + k) % 3] = slots[k];
    }
    nodes.forEach((node, i) => {
      const current = getNodeAngle(node);
      const next = current + normalizeAngleDelta(targets[i] - current);
      if (instant) node.style.transition = 'none';
      node.style.setProperty('--pdm-hiwt-node-angle', `${next}deg`);
      if (instant) {
        node.offsetHeight;
        node.style.transition = '';
      }
    });
  };

  const getNodeAngle = (node) => {
    const raw = node.style.getPropertyValue('--pdm-hiwt-node-angle');
    return parseFloat(raw) || 0;
  };

  /**
   * Rotates the whole wheel so `targetNode` lands on the current active
   * angle (90deg side by side / 180deg stacked), taking the shortest path
   * (never more than half a turn either direction). Pass `instant: true`
   * to skip the CSS transition — used on load/resize, so the wheel doesn't
   * visibly spin when the user didn't trigger the change themselves.
   */
  const rotateWheelTo = (nodes, targetNode, instant = false) => {
    const currentAngle = getNodeAngle(targetNode);
    const delta = normalizeAngleDelta(getActiveAngle(nodes.length) - currentAngle);
    if (delta === 0) return;

    nodes.forEach((node) => {
      const nextAngle = getNodeAngle(node) + delta;
      if (instant) node.style.transition = 'none';
      node.style.setProperty('--pdm-hiwt-node-angle', `${nextAngle}deg`);
      if (instant) {
        node.offsetHeight; // flush the instant jump before re-enabling the transition
        node.style.transition = '';
      }
    });
  };

  function initIngredientModal(modal, slideCount) {
    const swiperEl = modal.querySelector('[data-modal-swiper]');
    const prevEl = modal.querySelector('[data-modal-prev]');
    const nextEl = modal.querySelector('[data-modal-next]');
    const paginationEl = modal.querySelector('[data-modal-pagination]');
    const closeButton = modal.querySelector('.pdm-how-it-works-together__modal-close');

    let swiper = null;
    let lastTrigger = null;

    const getFocusableElements = () =>
      Array.from(modal.querySelectorAll('button, [href], [tabindex]:not([tabindex="-1"])'));

    const trapFocus = (event) => {
      if (event.key === 'Escape') {
        close();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = getFocusableElements();
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const open = (index, triggerNode) => {
      lastTrigger = triggerNode ?? null;
      modal.hidden = false;
      document.body.classList.add('pdm-hiwt-modal-open');
      document.addEventListener('keydown', trapFocus);

      if (!swiper && typeof Swiper !== 'undefined') {
        swiper = new Swiper(swiperEl, {
          slidesPerView: 1,
          spaceBetween: 0,
          loop: slideCount > 2,
          rewind: slideCount === 2,
          initialSlide: index,
          navigation: { prevEl, nextEl },
          pagination: { el: paginationEl, clickable: true },
          keyboard: { enabled: true },
        });
      } else if (swiper) {
        if (swiper.params.loop) {
          swiper.slideToLoop(index, 0);
        } else {
          swiper.slideTo(index, 0);
        }
      }

      requestAnimationFrame(() => closeButton?.focus());
    };

    function close() {
      modal.hidden = true;
      document.body.classList.remove('pdm-hiwt-modal-open');
      document.removeEventListener('keydown', trapFocus);
      lastTrigger?.focus();
    }

    modal.querySelectorAll('[data-modal-close]').forEach((element) => {
      element.addEventListener('click', close);
    });

    return { open, close };
  }

  function initHowItWorksTogether(section) {
    const wheel = section.querySelector('[data-wheel]');
    const nodes = Array.from(section.querySelectorAll('[data-wheel-node]'));
    const panels = Array.from(section.querySelectorAll('[data-detail-panel]'));
    const modal = section.querySelector('[data-ingredient-modal]');

    if (!wheel || nodes.length === 0) return;

    let modalController = null;

    const getActiveNode = () =>
      nodes.find((node) => node.classList.contains('pdm-how-it-works-together__node--active'));

    const setActivePanel = (index) => {
      panels.forEach((panel) => {
        const isMatch = panel.dataset.index === String(index);
        panel.hidden = !isMatch;
        panel.classList.toggle('pdm-how-it-works-together__card-panel--active', isMatch);
      });
    };

    const setActiveNode = (activeIndex) => {
      nodes.forEach((node) => {
        const isMatch = node.dataset.index === String(activeIndex);
        node.classList.toggle('pdm-how-it-works-together__node--active', isMatch);
        node.setAttribute('aria-pressed', String(isMatch));
      });
    };

    wheel.addEventListener('click', (event) => {
      const node = event.target.closest('[data-wheel-node]');
      if (!node || !nodes.includes(node)) return;

      const index = Number(node.dataset.index);

      if (isMobileViewport()) {
        if (!modal) return;
        modalController ??= initIngredientModal(modal, nodes.length);
        modalController.open(index, node);
        return;
      }

      if (nodes.length === 3) {
        placeThree(nodes, index);
      } else {
        rotateWheelTo(nodes, node);
      }
      setActiveNode(index);
      setActivePanel(index);
    });

    // Keep the active node aligned with the current active slot (90deg
    // side-by-side / 180deg stacked) on load, and re-sync if the viewport
    // is resized across STACKED_BREAKPOINT while the page is open.
    const syncActiveAngle = () => {
      const activeNode = getActiveNode();
      if (!activeNode) return;
      if (nodes.length === 3) {
        placeThree(nodes, nodes.indexOf(activeNode), true);
      } else {
        rotateWheelTo(nodes, activeNode, true);
      }
    };

    syncActiveAngle();

    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(syncActiveAngle, 150);
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    document
      .querySelectorAll('[data-pdm-how-it-works-together]')
      .forEach(initHowItWorksTogether);
  });

  // Re-initialize when the merchant edits the section in the theme editor.
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('[data-pdm-how-it-works-together]');
    if (section) initHowItWorksTogether(section);
  });
})();
